import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Radio,
  TextField,
  Typography,
} from '@mui/material';
import useApiContext from 'hooks/useApiContext';
import {
  iPublicTicketing,
  tFieldMode,
} from 'services/Ticketing/Ticketing.type';
import { countries } from './countries';
import { formatMoney } from './common';

const TEXT = {
  fr: {
    tickets: 'Billetterie',
    choose: 'Choisissez votre billet',
    free: 'Gratuit',
    soldOut: 'Complet',
    left: (n: number) => `${n} place(s) restante(s)`,
    you: 'Vos informations',
    firstname: 'Prénom',
    lastname: 'Nom',
    email: 'E-mail',
    emailHelp: 'Votre e-billet sera envoyé à cette adresse.',
    phone: 'Téléphone',
    company: 'Société',
    job_title: 'Fonction',
    industry: "Secteur d'activité",
    address: 'Adresse',
    postal_code: 'Code postal',
    city: 'Ville',
    country: 'Pays de résidence',
    nationality: 'Nationalité',
    more: 'Questions complémentaires',
    promo: 'Code promo',
    apply: 'Appliquer',
    promoOk: 'Code appliqué',
    total: 'Total',
    vat: 'TTC',
    terms:
      "J'accepte les conditions générales de vente et le traitement de mes données pour l'organisation du salon.",
    termsLink: 'Lire les conditions',
    optin: "J'accepte de recevoir des informations de l'organisateur.",
    pay: 'Payer et recevoir mon billet',
    get: 'Recevoir mon billet',
    closed: 'Les inscriptions en ligne sont fermées pour ce salon.',
    none: "Aucun billet n'est en vente pour le moment.",
    optional: 'facultatif',
    noPayment:
      "Le paiement en ligne n'est pas encore disponible : seuls les billets gratuits peuvent être pris.",
    validOn: 'Valable :',
    notFound: 'Billetterie introuvable ou fermée.',
  },
  en: {
    tickets: 'Tickets',
    choose: 'Choose your ticket',
    free: 'Free',
    soldOut: 'Sold out',
    left: (n: number) => `${n} left`,
    you: 'Your details',
    firstname: 'First name',
    lastname: 'Last name',
    email: 'E-mail',
    emailHelp: 'Your e-ticket will be sent to this address.',
    phone: 'Phone',
    company: 'Company',
    job_title: 'Job title',
    industry: 'Industry',
    address: 'Address',
    postal_code: 'Postcode',
    city: 'City',
    country: 'Country of residence',
    nationality: 'Nationality',
    more: 'A few more questions',
    promo: 'Promo code',
    apply: 'Apply',
    promoOk: 'Code applied',
    total: 'Total',
    vat: 'incl. VAT',
    terms:
      'I accept the terms of sale and the processing of my data for the organisation of the event.',
    termsLink: 'Read the terms',
    optin: 'I agree to receive news from the organiser.',
    pay: 'Pay and get my ticket',
    get: 'Get my ticket',
    closed: 'Online registration is closed for this event.',
    none: 'No tickets are on sale at the moment.',
    optional: 'optional',
    noPayment:
      'Online payment is not available yet: only free tickets can be booked.',
    validOn: 'Valid:',
    notFound: 'Ticketing not found or closed.',
  },
};

type Std =
  | 'phone'
  | 'company'
  | 'job_title'
  | 'industry'
  | 'address'
  | 'postal_code'
  | 'city'
  | 'country'
  | 'nationality';
const STD: Std[] = [
  'company',
  'job_title',
  'phone',
  'industry',
  'address',
  'postal_code',
  'city',
  'country',
  'nationality',
];

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
    };
  }
}

/** Public registration page of an event's ticketing (no login). */
export default function PublicRegister(): JSX.Element {
  const { slug = '' } = useParams();
  const company = window.location.pathname.split('/')[1];
  const { i18n } = useTranslation();
  const locale = i18n.language?.startsWith('en') ? 'en' : 'fr';
  const L = TEXT[locale];
  const intl = locale === 'en' ? 'en-GB' : 'fr-FR';
  const { ticketingRepository } = useApiContext();
  const [d, setD] = useState<iPublicTicketing | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [v, setV] = useState<Record<string, any>>({
    firstname: '',
    lastname: '',
    email: '',
    accept_terms: false,
    marketing_optin: false,
    promo_code: '',
    answers: {},
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [promo, setPromo] = useState<{
    total: number;
    discount: number;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [captcha, setCaptcha] = useState<string | null>(null);
  const captchaRef = useRef<HTMLDivElement>(null);
  const countryList = useMemo(() => countries(locale), [locale]);

  useEffect(() => {
    ticketingRepository
      .getPublic(company, slug, locale)
      .then((r) => {
        setD(r);
        const first = r.types.find(
          (t) =>
            t.remaining !== 0 &&
            (t.price_cents === 0 || r.settings.payments_available)
        );
        setV((x) => ({ ...x, ticket_type_id: first?.id }));
      })
      .catch((e: Error) => setLoadError(e.message));
  }, [slug, locale]);

  // Cloudflare Turnstile, only when the server asks for it
  useEffect(() => {
    const key = d?.settings.captcha_site_key;
    if (!key || !captchaRef.current) return;
    const render = (): void => {
      if (window.turnstile && captchaRef.current)
        window.turnstile.render(captchaRef.current, {
          sitekey: key,
          callback: (t: string) => setCaptcha(t),
        });
    };
    if (window.turnstile) {
      render();
      return;
    }
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    s.async = true;
    s.onload = render;
    document.head.appendChild(s);
  }, [d?.settings.captcha_site_key]);

  if (loadError)
    return (
      <Box sx={{ p: 4, maxWidth: 700, mx: 'auto' }}>
        <Alert severity="info">{L.notFound}</Alert>
      </Box>
    );
  if (!d)
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );

  const accent = d.settings.accent;
  const selected = d.types.find((t) => t.id === v.ticket_type_id);
  const total = promo ? promo.total : selected?.price_cents ?? 0;
  const set = (k: string, value: unknown): void => setV({ ...v, [k]: value });
  const dates = (() => {
    const f = (s: string | null): string =>
      s
        ? new Date(`${s.slice(0, 10)}T12:00:00`).toLocaleDateString(intl, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })
        : '';
    return d.event.date_of_the_end &&
      d.event.date_of_the_end.slice(0, 10) !== d.event.date?.slice(0, 10)
      ? `${f(d.event.date)} – ${f(d.event.date_of_the_end)}`
      : f(d.event.date);
  })();

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    setBusy(true);
    setErrors({});
    setError(null);
    ticketingRepository
      .register(company, slug, {
        ...v,
        locale,
        origin: window.location.origin,
        captcha_token: captcha,
      })
      .then((r) => {
        if (r.checkout_url) window.location.href = r.checkout_url;
        else
          window.location.href = `/${company}/tickets/${slug}/order/${r.reference}?k=${r.key}`;
      })
      .catch((err: Error & { errors?: Record<string, string[]> }) => {
        setBusy(false);
        if (err.errors)
          setErrors(
            Object.fromEntries(
              Object.entries(err.errors).map(([k, m]) => [k, m[0]])
            )
          );
        setError(err.message);
      });
  };

  const field = (name: Std): JSX.Element | null => {
    const mode: tFieldMode = d.settings.fields[name];
    if (mode === 'hidden') return null;
    const required = mode === 'required';
    const label = `${L[name]}${required ? '' : ` (${L.optional})`}`;
    if (name === 'country' || name === 'nationality') {
      return (
        <TextField
          key={name}
          select
          size="small"
          label={label}
          required={required}
          value={v[name] ?? ''}
          onChange={(e) => set(name, e.target.value)}
          error={!!errors[name]}
          helperText={errors[name]}
        >
          <MenuItem value="">—</MenuItem>
          {countryList.map((c) => (
            <MenuItem key={c.code} value={c.code}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    return (
      <TextField
        key={name}
        size="small"
        label={label}
        required={required}
        value={v[name] ?? ''}
        onChange={(e) => set(name, e.target.value)}
        type={name === 'phone' ? 'tel' : 'text'}
        error={!!errors[name]}
        helperText={errors[name]}
        sx={name === 'address' ? { gridColumn: '1 / -1' } : undefined}
      />
    );
  };
  const section = {
    bgcolor: '#fff',
    border: '1px solid #eee',
    borderRadius: '12px',
    p: { xs: 2, sm: 3 },
    mb: 2,
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f9fafb' }}>
      <Box
        sx={{
          bgcolor: accent,
          color: '#fff',
          ...(d.settings.banner_url
            ? {
                backgroundImage: `linear-gradient(rgba(0,0,0,.38), rgba(0,0,0,.38)), url(${d.settings.banner_url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : {}),
        }}
      >
        <Box
          sx={{
            maxWidth: 760,
            mx: 'auto',
            px: 2,
            py: 5,
            display: 'flex',
            gap: 3,
            alignItems: 'center',
          }}
        >
          {d.settings.logo_url && (
            <img
              src={d.settings.logo_url}
              alt=""
              style={{
                height: 64,
                maxWidth: 160,
                objectFit: 'contain',
                // PNG: transparent, straight on the banner
                ...(d.settings.logo_transparent
                  ? {}
                  : { background: '#fff', padding: 6 }),
              }}
            />
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 12,
                letterSpacing: 2,
                textTransform: 'uppercase',
                opacity: 0.8,
              }}
            >
              {L.tickets}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 26, sm: 32 },
                fontWeight: 700,
                lineHeight: 1.15,
              }}
            >
              {d.settings.title}
            </Typography>
            {d.settings.subtitle && (
              <Typography sx={{ fontSize: { xs: 16, sm: 18 }, mt: 0.5 }}>
                {d.settings.subtitle}
              </Typography>
            )}
            <Typography sx={{ opacity: 0.9, mt: 0.5 }}>
              {dates}
              {d.event.city ? ` · ${d.event.city}` : ''}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 760, mx: 'auto', px: 2, py: 4 }}>
        {d.settings.intro && (
          <Typography sx={{ whiteSpace: 'pre-line', mb: 3, color: '#374151' }}>
            {d.settings.intro}
          </Typography>
        )}
        {!d.settings.sales_open || d.types.length === 0 ? (
          <Box sx={section}>{d.settings.sales_open ? L.none : L.closed}</Box>
        ) : (
          <form onSubmit={submit}>
            <Box sx={section}>
              <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 2 }}>
                {L.choose}
              </Typography>
              {!d.settings.payments_available &&
                d.types.some((t) => t.price_cents > 0) && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {L.noPayment}
                  </Alert>
                )}
              {d.types.map((t) => {
                const disabled =
                  t.remaining === 0 ||
                  (t.price_cents > 0 && !d.settings.payments_available);
                const active = v.ticket_type_id === t.id;
                return (
                  <Box
                    key={t.id}
                    component="label"
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      p: 2,
                      mb: 1.5,
                      borderRadius: '12px',
                      border: '2px solid',
                      borderColor: active ? accent : '#e5e7eb',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.5 : 1,
                    }}
                  >
                    <Radio
                      checked={active}
                      disabled={disabled}
                      onChange={() => {
                        set('ticket_type_id', t.id);
                        setPromo(null);
                      }}
                      sx={{ p: 0, mt: 0.3, '&.Mui-checked': { color: accent } }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 2,
                        }}
                      >
                        <Typography sx={{ fontWeight: 700 }}>
                          {t.name}
                        </Typography>
                        <Typography
                          sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                        >
                          {t.remaining === 0
                            ? L.soldOut
                            : t.price_cents === 0
                            ? L.free
                            : formatMoney(
                                t.price_cents,
                                d.settings.currency,
                                intl
                              )}
                        </Typography>
                      </Box>
                      {t.description && (
                        <Typography
                          sx={{
                            fontSize: 14,
                            color: '#555',
                            whiteSpace: 'pre-line',
                          }}
                        >
                          {t.description}
                        </Typography>
                      )}
                      <Typography sx={{ fontSize: 12, color: '#777', mt: 0.5 }}>
                        {L.validOn} {t.days.join(' · ')}
                      </Typography>
                      {t.remaining !== null &&
                        t.remaining > 0 &&
                        t.remaining <= 50 && (
                          <Typography sx={{ fontSize: 12, color: '#b45309' }}>
                            {L.left(t.remaining)}
                          </Typography>
                        )}
                    </Box>
                  </Box>
                );
              })}
              {errors.ticket_type_id && (
                <Typography sx={{ color: '#dc2626', fontSize: 14 }}>
                  {errors.ticket_type_id}
                </Typography>
              )}
            </Box>

            <Box sx={section}>
              <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 2 }}>
                {L.you}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                }}
              >
                <TextField
                  size="small"
                  required
                  label={L.firstname}
                  autoComplete="given-name"
                  value={v.firstname}
                  onChange={(e) => set('firstname', e.target.value)}
                  error={!!errors.firstname}
                  helperText={errors.firstname}
                />
                <TextField
                  size="small"
                  required
                  label={L.lastname}
                  autoComplete="family-name"
                  value={v.lastname}
                  onChange={(e) => set('lastname', e.target.value)}
                  error={!!errors.lastname}
                  helperText={errors.lastname}
                />
                <TextField
                  size="small"
                  required
                  type="email"
                  label={L.email}
                  autoComplete="email"
                  value={v.email}
                  onChange={(e) => set('email', e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email ?? L.emailHelp}
                  sx={{ gridColumn: '1 / -1' }}
                />
                {STD.map(field)}
              </Box>
            </Box>

            {d.settings.questions.length > 0 && (
              <Box
                sx={{
                  ...section,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
                  {L.more}
                </Typography>
                {d.settings.questions.map((q) => {
                  const label =
                    locale === 'en' && q.label_en ? q.label_en : q.label_fr;
                  const err = errors[`answers.${q.id}`];
                  const setAnswer = (value: unknown): void =>
                    set('answers', { ...v.answers, [q.id]: value });
                  if (q.type === 'checkbox')
                    return (
                      <FormControlLabel
                        key={q.id}
                        control={
                          <Checkbox
                            checked={!!v.answers[q.id]}
                            required={q.required}
                            onChange={(e) => setAnswer(e.target.checked)}
                          />
                        }
                        label={label}
                      />
                    );
                  return q.type === 'select' ? (
                    <TextField
                      key={q.id}
                      select
                      size="small"
                      label={label}
                      required={q.required}
                      value={v.answers[q.id] ?? ''}
                      onChange={(e) => setAnswer(e.target.value)}
                      error={!!err}
                      helperText={err}
                    >
                      <MenuItem value="">—</MenuItem>
                      {q.options.map((o) => (
                        <MenuItem key={o} value={o}>
                          {o}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <TextField
                      key={q.id}
                      size="small"
                      label={label}
                      required={q.required}
                      value={v.answers[q.id] ?? ''}
                      onChange={(e) => setAnswer(e.target.value)}
                      error={!!err}
                      helperText={err}
                    />
                  );
                })}
              </Box>
            )}

            <Box sx={section}>
              {d.settings.has_promo_codes &&
                selected &&
                selected.price_cents > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      size="small"
                      label={L.promo}
                      value={v.promo_code}
                      onChange={(e) => {
                        set('promo_code', e.target.value.toUpperCase());
                        setPromo(null);
                      }}
                      error={!!errors.promo_code}
                      helperText={errors.promo_code}
                    />
                    <Button
                      variant="outlined"
                      disabled={!v.promo_code}
                      sx={{ textTransform: 'none' }}
                      onClick={async () =>
                        await ticketingRepository
                          .checkPromo(
                            company,
                            slug,
                            v.promo_code,
                            v.ticket_type_id,
                            locale
                          )
                          .then((r) => {
                            setPromo({
                              total: r.total_cents,
                              discount: r.discount_cents,
                            });
                            setErrors({ ...errors, promo_code: '' });
                          })
                          .catch((er: Error) => {
                            setPromo(null);
                            setErrors({ ...errors, promo_code: er.message });
                          })
                      }
                    >
                      {L.apply}
                    </Button>
                  </Box>
                )}
              {promo && (
                <Typography sx={{ color: '#15803d', fontSize: 14, mb: 1 }}>
                  ✓ {L.promoOk} (−
                  {formatMoney(promo.discount, d.settings.currency, intl)})
                </Typography>
              )}
              <FormControlLabel
                sx={{ alignItems: 'flex-start' }}
                control={
                  <Checkbox
                    required
                    checked={v.accept_terms}
                    onChange={(e) => set('accept_terms', e.target.checked)}
                    sx={{ pt: 0.3 }}
                  />
                }
                label={
                  <span style={{ fontSize: 14 }}>
                    {L.terms}
                    {d.settings.terms_url && (
                      <>
                        {' '}
                        <a
                          href={d.settings.terms_url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {L.termsLink}
                        </a>
                      </>
                    )}
                  </span>
                }
              />
              <FormControlLabel
                sx={{ alignItems: 'flex-start' }}
                control={
                  <Checkbox
                    checked={v.marketing_optin}
                    onChange={(e) => set('marketing_optin', e.target.checked)}
                    sx={{ pt: 0.3 }}
                  />
                }
                label={
                  <span style={{ fontSize: 14, color: '#555' }}>{L.optin}</span>
                }
              />
              {d.settings.captcha_site_key && (
                <div ref={captchaRef} style={{ marginTop: 8 }} />
              )}
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                  borderTop: '1px solid #eee',
                  pt: 2,
                  mt: 2,
                }}
              >
                <Typography sx={{ fontSize: 18 }}>
                  {L.total} :{' '}
                  <b>
                    {total === 0
                      ? L.free
                      : formatMoney(total, d.settings.currency, intl)}
                  </b>
                  {total > 0 && (
                    <span style={{ fontSize: 13, color: '#777' }}>
                      {' '}
                      {L.vat}
                    </span>
                  )}
                </Typography>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={busy || !selected}
                  sx={{
                    bgcolor: accent,
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 3,
                    py: 1.2,
                    '&:hover': { bgcolor: accent, filter: 'brightness(.9)' },
                  }}
                >
                  {busy ? (
                    <CircularProgress size={20} sx={{ color: '#fff' }} />
                  ) : total > 0 ? (
                    L.pay
                  ) : (
                    L.get
                  )}
                </Button>
              </Box>
            </Box>
          </form>
        )}
      </Box>
    </Box>
  );
}
